import { supabase } from '../lib/supabase'
import type {
  SoftDeleteParams,
  SupplierQuote,
  SupplierQuoteFilters,
  SupplierQuoteInput,
  SupplierQuoteItem,
  SupplierQuoteItemInput,
  SupplierQuoteStatus,
} from '../types/supplierQuotes'

const QUOTE_SELECT = `
  *,
  supplier:suppliers(id, name),
  request:marketing_requests(id, request_number, title),
  created_by_user:profiles!supplier_quotes_created_by_fkey(id, name),
  deleted_by_user:profiles!supplier_quotes_deleted_by_fkey(id, name),
  items:supplier_quotes_items(
    *,
    stock_item:marketing_stock_items(id, name, code, unit)
  )
`

function softDeletePayload(params?: SoftDeleteParams) {
  return {
    deleted_at: new Date().toISOString(),
    deleted_by: params?.deleted_by ?? null,
    deleted_reason: params?.deleted_reason ?? null,
  }
}

async function resolveActorId(explicit?: string | null): Promise<string | null> {
  const trimmed = explicit?.trim() || ''
  if (trimmed) return trimmed
  const { data, error } = await supabase.auth.getUser()
  if (error) return null
  return data.user?.id ?? null
}

function emptyToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() || ''
  return trimmed || null
}

function normalizeNonNegativeMoney(value: number | null | undefined, label: string): number {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n) || n < 0) {
    throw new Error(`Valor inválido para ${label}.`)
  }
  return Math.round(n * 10000) / 10000
}

function mapQuote(row: SupplierQuote): SupplierQuote {
  const items = (row.items || [])
    .filter((item) => !item.deleted_at)
    .sort((a, b) => a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at))
    .map((item) => ({
      ...item,
      quantity: Number(item.quantity),
      unit_value: Number(item.unit_value),
    }))

  return {
    ...row,
    freight_value: Number(row.freight_value || 0),
    discount_value: Number(row.discount_value || 0),
    items,
    invoice: row.invoice ?? null,
  }
}

async function attachInvoices(quotes: SupplierQuote[]): Promise<SupplierQuote[]> {
  if (!quotes.length) return quotes
  const ids = quotes.map((q) => q.id)
  const { data, error } = await supabase
    .from('invoices')
    .select('id, number, status, document_type, supplier_quote_id')
    .in('supplier_quote_id', ids)
    .is('deleted_at', null)
  if (error) throw error

  const byQuote = new Map<string, SupplierQuote['invoice']>()
  for (const row of data || []) {
    if (!row.supplier_quote_id || byQuote.has(row.supplier_quote_id)) continue
    byQuote.set(row.supplier_quote_id, {
      id: row.id,
      number: row.number,
      status: row.status,
      document_type: row.document_type,
    })
  }
  return quotes.map((quote) => ({
    ...quote,
    invoice: byQuote.get(quote.id) || null,
  }))
}

function validateItems(items: SupplierQuoteItemInput[]) {
  if (!items?.length) {
    throw new Error('Informe ao menos um item na proposta.')
  }
  for (const [index, item] of items.entries()) {
    const qty = Number(item.quantity)
    const unitValue = Number(item.unit_value)
    if (!Number.isFinite(qty) || qty <= 0) {
      throw new Error(`Quantidade inválida no item ${index + 1}.`)
    }
    if (!Number.isFinite(unitValue) || unitValue < 0) {
      throw new Error(`Preço unitário inválido no item ${index + 1}.`)
    }
    const hasStock = Boolean(item.stock_item_id?.trim())
    const hasDescription = Boolean(item.description?.trim())
    if (!hasStock && !hasDescription) {
      throw new Error(`Informe a descrição ou o item de estoque na linha ${index + 1}.`)
    }
  }
}

function itemRows(quoteId: string, items: SupplierQuoteItemInput[]) {
  return items.map((item, index) => ({
    quote_id: quoteId,
    sort_order: item.sort_order ?? index * 10,
    stock_item_id: emptyToNull(item.stock_item_id),
    request_item_id: emptyToNull(item.request_item_id),
    description: emptyToNull(item.description),
    quantity: Number(item.quantity),
    unit: emptyToNull(item.unit),
    unit_value: normalizeNonNegativeMoney(item.unit_value, 'preço unitário'),
  }))
}

export function supplierQuoteItemsTotal(quote: Pick<SupplierQuote, 'items' | 'freight_value' | 'discount_value'>): number {
  const itemsSum = (quote.items || []).reduce(
    (acc, item) => acc + Number(item.quantity || 0) * Number(item.unit_value || 0),
    0
  )
  const total =
    itemsSum + Number(quote.freight_value || 0) - Number(quote.discount_value || 0)
  return Math.round(Math.max(0, total) * 10000) / 10000
}

export const supplierQuotesService = {
  async list(filters?: SupplierQuoteFilters): Promise<SupplierQuote[]> {
    let query = supabase
      .from('supplier_quotes')
      .select(QUOTE_SELECT)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }
    if (filters?.supplierId) {
      query = query.eq('supplier_id', filters.supplierId)
    }
    if (filters?.requestId) {
      query = query.eq('request_id', filters.requestId)
    }
    if (filters?.dateFrom) {
      query = query.gte('created_at', `${filters.dateFrom}T00:00:00`)
    }
    if (filters?.dateTo) {
      query = query.lte('created_at', `${filters.dateTo}T23:59:59`)
    }

    const { data, error } = await query
    if (error) throw error

    let rows = await attachInvoices((data || []).map((row) => mapQuote(row as SupplierQuote)))
    const term = filters?.search?.trim().toLowerCase()
    if (term) {
      rows = rows.filter((row) => {
        const haystack = [
          String(row.quote_number),
          row.title,
          row.supplier?.name || '',
          row.request?.request_number != null ? String(row.request.request_number) : '',
        ]
          .join(' ')
          .toLowerCase()
        return haystack.includes(term)
      })
    }
    return rows
  },

  async listByRequest(requestId: string): Promise<SupplierQuote[]> {
    if (!requestId) return []
    return this.list({ requestId })
  },

  async get(id: string): Promise<SupplierQuote | null> {
    const { data, error } = await supabase
      .from('supplier_quotes')
      .select(QUOTE_SELECT)
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    const [mapped] = await attachInvoices([mapQuote(data as SupplierQuote)])
    return mapped
  },

  async create(input: SupplierQuoteInput, createdBy?: string | null): Promise<SupplierQuote> {
    if (!input.supplier_id?.trim()) {
      throw new Error('Selecione o fornecedor.')
    }
    if (!input.title?.trim()) {
      throw new Error('Informe o título da proposta.')
    }
    validateItems(input.items)

    const freightValue = normalizeNonNegativeMoney(input.freight_value, 'frete')
    const discountValue = normalizeNonNegativeMoney(input.discount_value, 'desconto')
    const status: SupplierQuoteStatus = input.status || 'rascunho'
    const actorId = await resolveActorId(createdBy)

    const { data: header, error: headerError } = await supabase
      .from('supplier_quotes')
      .insert({
        supplier_id: input.supplier_id,
        request_id: emptyToNull(input.request_id),
        title: input.title.trim(),
        status,
        freight_value: freightValue,
        discount_value: discountValue,
        valid_until: emptyToNull(input.valid_until),
        delivery_deadline: emptyToNull(input.delivery_deadline),
        notes: emptyToNull(input.notes),
        created_by: actorId,
      })
      .select('id')
      .single()

    if (headerError) throw headerError

    const { error: itemsError } = await supabase
      .from('supplier_quotes_items')
      .insert(itemRows(header.id, input.items))

    if (itemsError) {
      await supabase
        .from('supplier_quotes')
        .update(
          softDeletePayload({
            deleted_by: actorId,
            deleted_reason: 'rollback create items',
          })
        )
        .eq('id', header.id)
      throw itemsError
    }

    const created = await this.get(header.id)
    if (!created) throw new Error('Proposta criada, mas não foi possível recarregar.')
    return created
  },

  async update(id: string, input: SupplierQuoteInput): Promise<SupplierQuote> {
    const existing = await this.get(id)
    if (!existing) throw new Error('Proposta não encontrada.')
    if (existing.status === 'nota_gerada') {
      throw new Error('Proposta vinculada a nota confirmada/gerada não pode ser editada.')
    }
    if (!input.supplier_id?.trim()) {
      throw new Error('Selecione o fornecedor.')
    }
    if (!input.title?.trim()) {
      throw new Error('Informe o título da proposta.')
    }
    validateItems(input.items)

    const freightValue = normalizeNonNegativeMoney(input.freight_value, 'frete')
    const discountValue = normalizeNonNegativeMoney(input.discount_value, 'desconto')
    const status: SupplierQuoteStatus = input.status || existing.status

    const { error: headerError } = await supabase
      .from('supplier_quotes')
      .update({
        supplier_id: input.supplier_id,
        request_id: emptyToNull(input.request_id),
        title: input.title.trim(),
        status,
        freight_value: freightValue,
        discount_value: discountValue,
        valid_until: emptyToNull(input.valid_until),
        delivery_deadline: emptyToNull(input.delivery_deadline),
        notes: emptyToNull(input.notes),
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (headerError) throw headerError

    const { error: softItemsError } = await supabase
      .from('supplier_quotes_items')
      .update(softDeletePayload({ deleted_reason: 'replace items on update' }))
      .eq('quote_id', id)
      .is('deleted_at', null)

    if (softItemsError) throw softItemsError

    const { error: itemsError } = await supabase
      .from('supplier_quotes_items')
      .insert(itemRows(id, input.items))

    if (itemsError) throw itemsError

    const updated = await this.get(id)
    if (!updated) throw new Error('Proposta atualizada, mas não foi possível recarregar.')
    return updated
  },

  async setStatus(id: string, status: SupplierQuoteStatus): Promise<SupplierQuote> {
    const { error } = await supabase
      .from('supplier_quotes')
      .update({ status })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
    const updated = await this.get(id)
    if (!updated) throw new Error('Proposta não encontrada.')
    return updated
  },

  async softDelete(id: string, params?: SoftDeleteParams): Promise<void> {
    const existing = await this.get(id)
    if (!existing) throw new Error('Proposta não encontrada.')
    if (existing.deleted_at) {
      throw new Error('Proposta já excluída.')
    }
    if (existing.status === 'nota_gerada') {
      throw new Error('Proposta vinculada a nota não pode ser excluída.')
    }

    const deletedBy = await resolveActorId(params?.deleted_by)
    const payload = softDeletePayload({
      deleted_by: deletedBy,
      deleted_reason: params?.deleted_reason?.trim() || 'Exclusão lógica pela tela de propostas',
    })

    const { error: itemsError } = await supabase
      .from('supplier_quotes_items')
      .update(payload)
      .eq('quote_id', id)
      .is('deleted_at', null)
    if (itemsError) throw itemsError

    const { error } = await supabase
      .from('supplier_quotes')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error
  },
}

export type { SupplierQuoteItem }
